import React, { useCallback, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionTitle from "../components/SectionTitle";
import CTA from "../components/CTA";
import useSmoothScroll from "../hooks/useSmoothScroll";
import useScrollFx from "../hooks/useScrollFx";
import "../css/Gallery.css";

gsap.registerPlugin(ScrollTrigger);

// Every image in /public/assets. Add or remove numbers/extensions here.
// Images that fail to load are hidden automatically, so gaps in the
// numbering (e.g. no 2.png or 8.png) are safe.
const IMAGE_IDS = Array.from({ length: 29 }, (_, i) => i + 1);
const ALL_IMAGES = IMAGE_IDS.map((n) => ({
  src: `/assets/${n}.png`,
  alt: `Anappuram wooden furniture work ${n}`,
}));

export default function Gallery() {
  const root = useRef(null);
  const [broken, setBroken] = useState(() => new Set());
  const [active, setActive] = useState(null); // index in `images`
  useSmoothScroll();

  const images = ALL_IMAGES.filter((img) => !broken.has(img.src));

  const markBroken = (src) => setBroken((prev) => new Set(prev).add(src));

  // Masonry heights change as images load, so keep triggers accurate
  const refreshTimer = useRef(null);
  const onImgLoad = () => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => ScrollTrigger.refresh(), 150);
  };
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Gallery-lighting: a warm spotlight follows the cursor across the wall
  const onWallMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  useScrollFx(root, (g) => {
    // Hero intro
    const tl = g.timeline({ defaults: { ease: "power4.out" } });
    tl.from(".gal-hero__img", { scale: 1.35, duration: 2, ease: "power3.out" })
      .from(
        ".gal-hero .hero__eyebrow",
        { y: 20, opacity: 0, duration: 0.8 },
        0.3,
      )
      .from(".gal-line > span", { yPercent: 115, duration: 1.2 }, 0.4)
      .from(".gal-hero p", { y: 30, opacity: 0, duration: 0.9 }, 0.9);

    // Hero parallax
    const st = {
      trigger: ".gal-hero",
      start: "top top",
      end: "bottom top",
      scrub: true,
    };
    g.to(".gal-hero__img", {
      yPercent: 22,
      scale: 1.18,
      ease: "none",
      scrollTrigger: st,
    });
    g.to(".gal-hero__inner", {
      yPercent: -20,
      opacity: 0,
      ease: "none",
      scrollTrigger: st,
    });

    // Frames are "hung" on the wall: they drop in and swing to rest
    ScrollTrigger.batch(".gal-item", {
      start: "top 92%",
      once: true,
      onEnter: (els) =>
        g.from(els, {
          y: -70,
          opacity: 0,
          rotation: () => g.utils.random(-7, 7),
          transformOrigin: "50% 0%",
          duration: 1.6,
          stagger: 0.08,
          ease: "elastic.out(1, 0.45)",
          clearProps: "transform,opacity",
        }),
    });
  });

  // ---------- Lightbox ----------
  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) =>
      setActive((i) =>
        i === null ? i : (i + dir + images.length) % images.length,
      ),
    [images.length],
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close, step]);

  return (
    <div ref={root}>
      <div className="scroll-progress" aria-hidden="true" />

      <section className="page-hero gal-hero">
        <img
          className="gal-hero__img"
          src="/assets/24.png"
          alt=""
          loading="eager"
        />
        <div className="gal-hero__overlay" />

        {/* Ceiling track lights that switch on one by one */}
        <div className="gal-track" aria-hidden="true">
          {[0, 1, 2, 3].map((n) => (
            <span
              key={n}
              className="gal-lamp"
              style={{ "--on": `${0.6 + n * 0.35}s` }}
            />
          ))}
        </div>

        <div className="container gal-hero__inner">
          <span className="hero__eyebrow">Our Work</span>
          <h1>
            <span className="gal-line">
              <span>Gallery</span>
            </span>
          </h1>
          <p>
            A look at wardrobes, kitchens, TV units, panelling and custom
            furniture handcrafted at our workshop.
          </p>
        </div>
      </section>

      <section className="section gal-wall" onMouseMove={onWallMove}>
        <div className="gal-wall__light" aria-hidden="true" />
        <div className="container">
          <SectionTitle
            eyebrow="Crafted in Palakkad"
            title="Featured Work"
            description="Tap any photo to view it full size."
            center
          />

          <div className="gal-masonry">
            {images.map((img, i) => (
              <figure className="gal-item" key={img.src}>
                <button
                  type="button"
                  className="gal-item__btn"
                  onClick={() => setActive(i)}
                  aria-label={`Open image ${i + 1} full size`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    onLoad={onImgLoad}
                    onError={() => markBroken(img.src)}
                  />
                  <span className="gal-item__zoom" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-4.3-4.3M11 8v6M8 11h6"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <CTA
          eyebrow="Start Your Project"
          title="Like What You See?"
          description="Call or WhatsApp us from anywhere in India and let's build yours."
        />
      </section>

      {/* ---------- Lightbox ---------- */}
      {active !== null && images[active] && (
        <div
          className="gal-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          onClick={close}
        >
          <button
            type="button"
            className="gal-lightbox__close"
            onClick={close}
            aria-label="Close"
          >
            &times;
          </button>
          <button
            type="button"
            className="gal-lightbox__nav gal-lightbox__nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous image"
          >
            &#8249;
          </button>

          <div
            className="gal-lightbox__frame"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              className="gal-lightbox__img"
              src={images[active].src}
              alt={images[active].alt}
            />
            <span className="gal-lightbox__plaque">
              Handcrafted in Palakkad &nbsp;|&nbsp; {active + 1} of{" "}
              {images.length}
            </span>
          </div>

          <button
            type="button"
            className="gal-lightbox__nav gal-lightbox__nav--next"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next image"
          >
            &#8250;
          </button>
        </div>
      )}
    </div>
  );
}
